import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Button, 
  Card, 
  CardActions, 
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  InputLabel,
  OutlinedInput,
  Stack,
  FormHelperText,
  Grid,
  Box,
  IconButton,
  MenuItem,
  Chip,
  SelectChangeEvent,
  Typography
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { Business } from '../../../types/UserTypes';
import axios from '../../../../services/ApiService';
import LoadingSpinner from '../../../support/LoadingSpinner';
import toast from 'react-hot-toast';
import { BVTConstants } from '../../../../utils/BVTConstants';
import { CompanyCategory } from '../../../types/DataTypes';
import { paths } from '../../../../utils/paths';
import { Edit } from '@mui/icons-material';
import { useAuthContext } from '../../../../hooks/useAuthContext';

export function BusinessCorporateProfile(): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = (location.state as { userId?: number })?.userId;
  const { user } = useAuthContext();
  const [loading, setLoading] = useState(false);
  const [sectors, setSectors] = useState<CompanyCategory[]>([]);
  const [businessData, setBusinessData] = useState<Business | null>(null);

  useEffect(() => {
    fetchBusinessData();
    fetchSectors();
  }, []);

  const fetchBusinessData = async () => {
    try {
      setLoading(true);
      let response;
      if (userId) {
        response = await axios.post("/getBusinessByUserId", userId);
      } else {
        response = await axios.get("/getBusiness");
      }
      setBusinessData(response.data);
    } catch (error) {
      console.error('Error fetching business data.');
      toast.error('Failed to load business data.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSectors = async () => {
    setSectors([]);
    setLoading(true);
    try {
      const response = await axios.get('/getCompanyCategories');
      if (Array.isArray(response.data)) {
        setSectors(response.data);
      }
    } catch (error) {
      console.error('Error fetching sectors.');
    } finally {
      setLoading(false);
    }
  };

  const getSectorName = (sectorId: number) => {
    const sector = sectors.find(sec => sec.catId === sectorId);
    return sector ? sector.catName : 'N/A';
  };

  return (
    <>
      <Helmet><title>BVT Business - Corporate Profile</title></Helmet>
      <Stack spacing={3}>
        <Card>
          <CardHeader 
            title="Corporate Profile"
            action={
              <>
                {(user?.role === BVTConstants.USER_TYPE_BUSINESS || user?.role === BVTConstants.USER_TYPE_ADMIN) && (
                  <IconButton 
                    aria-label="edit" 
                    onClick={() => navigate(paths.business.editCorporateProfile, { state: { userId: businessData?.userId } })}
                    sx={{ 
                      border: '1px solid',
                      borderColor: 'divider',
                      ml: 1
                    }}
                  >
                    <Edit />
                  </IconButton>
                )}
              </>
            }
          />
          <Divider />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Sector
                </Typography>
                <Stack spacing={1}>
                  <Chip 
                    label={getSectorName(businessData?.sectorId || 0)}
                    size="small"
                    sx={{ width: 'fit-content' }}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Business Type
                </Typography>
                <Typography variant="body1">
                  {businessData?.businessType || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Business Size
                </Typography>
                <Typography variant="body1">
                  {businessData?.businessSize || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Area of Support Required
                </Typography>
                <Stack spacing={1}>
                  {businessData?.supportRequired && businessData.supportRequired.length > 0 ? (
                    businessData.supportRequired.map((support, index) => (
                      <Chip 
                        key={index} 
                        label={support} 
                        size="small"
                        sx={{ width: 'fit-content' }}
                      />
                    ))
                  ) : (
                    <Typography variant="body1">N/A</Typography>
                  )}
                </Stack>
              </Grid>

              {businessData?.businessType === BVTConstants.BUSINESS_TYPE_ABORIGINAL_CORPORATION && (
                <>
                  <Grid item xs={12}>
                    <Box sx={{ position: 'relative', my: 2 }}>
                      <Divider />
                      <Typography 
                        variant="subtitle1" 
                        color="primary" 
                        sx={{ 
                          position: 'absolute',
                          left: 10,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          backgroundColor: 'background.paper',
                          px: 1
                        }}
                      >
                        ORIC
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      ACN/ICN
                    </Typography>
                    <Typography variant="body1">
                      {businessData?.acnIcn || 'N/A'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Consolidated Gross Income
                    </Typography>
                    <Typography variant="body1">
                      {businessData?.grossIncome || 'N/A'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Consolidated Gross Assets
                    </Typography>
                    <Typography variant="body1">
                      {businessData?.grossAssets || 'N/A'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Number ofEmployees
                    </Typography>
                    <Typography variant="body1">
                      {businessData?.employees || 'N/A'}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>
        <LoadingSpinner loading={loading} />
      </Stack>
    </>
  );
}
